import { useState } from "react";
import { LuArrowLeft, LuBot, LuCoins, LuFlag, LuGamepad2, LuInfo, LuPlay, LuShuffle, LuSlidersHorizontal, LuTrophy, LuUser, LuUsers, LuZap } from "react-icons/lu";
import { DIFFICULTY_LABEL, GAMES, GAME_IDS, GRADES, SUBJECTS, gradeLabel } from "../../shared/constants";
import { ApiError, get, post } from "../api";
import { GAME_GROUPS, GameTile, type GameRule } from "../components/GameTile";
import { Floaties, Sparkles } from "../components/fx";
import { GameVideo } from "../components/GameVideo";
import { HeroScene } from "../components/HeroScene";
import { HowTo } from "../components/HowTo";
import { ErrorBox, GAME_META, GAME_ICONS, Spinner, Coin } from "../components/ui";
import { Link, go } from "../router";
import { useApp, useLoad } from "../store";
import { DEFAULT_MAP, type MapId } from "../../shared/games/taarn";
import { DifficultyPicker, MapPicker } from "./TaarnSetup";

const MODES: Record<string, string> = { solo: "Alene", bot: "Mod computer", quick: "Hurtig-match (online)", lobby: "Privat rum med kode" };

const FILTERS = [
  { id: "alle", label: "Alle spil", icon: LuGamepad2 },
  { id: "solo", label: "Alene", icon: LuUser },
  { id: "bot", label: "Mod computer", icon: LuBot },
  { id: "online", label: "Med andre online", icon: LuUsers },
] as const;
type FilterId = (typeof FILTERS)[number]["id"];

export function GameList() {
  const { me } = useApp();
  const meta = useLoad(() => get("/api/meta"), []);
  const [filter, setFilter] = useState<FilterId>("alle");
  /* the youngest pupils (0.–3. klasse) get the games first; the filters hide behind one "more choices" button */
  const little = (me!.grade ?? 9) <= 3;
  const [moreOpts, setMoreOpts] = useState(false);
  const rules: Record<string, GameRule> = meta.data?.rules ?? {};
  const ok = (g: string) => filter === "alle" || !rules[g] || !!rules[g]![filter];
  const shown = GAME_IDS.filter(ok);
  const surprise = () => {
    const pool = shown.length ? shown : GAME_IDS;
    const pick = pool[Math.floor(Math.random() * pool.length)]!;
    go(pick === "verden" ? "/verden" : `/spil/${pick}`);
  };
  const active = me!.activeMatch;
  return (
    <div className="stack gamelist rise">
      <section className="games-hero" aria-labelledby="gl-h" data-spot>
        <HeroScene palette="day" />
        <Sparkles n={16} />
        <Floaties kinds={["star", "cube", "coin"]} n={6} seed={5} />
        <div className="gh-body">
          <h1 id="gl-h" className="title-game">Hvad vil du spille?</h1>
          <p>Tryk på et spil!</p>
          <button type="button" className="btn cta" onClick={surprise}><LuShuffle aria-hidden="true" size={22} /> Overrask mig</button>
        </div>
      </section>

      {active && (
        <div className="live-banner good" role="status">
          <span className="lb-ico"><LuPlay aria-hidden="true" size={22} /></span>
          <span className="lb-text">Du har et {active.status === "lobby" ? "ventende" : "igangværende"} spil: <strong>{GAME_META[active.game]?.name}</strong></span>
          <Link to={`/kamp/${active.id}`} className="btn lime">Fortsæt her</Link>
        </div>
      )}

      {little && !moreOpts && filter === "alle" ? (
        <div className="gl-more">
          <button type="button" className="fchip" aria-expanded="false" onClick={() => setMoreOpts(true)}>
            <LuSlidersHorizontal aria-hidden="true" size={20} /> Flere valg
          </button>
        </div>
      ) : (
        <div className="filters gl-filters" role="group" aria-label="Vis spil">
          {FILTERS.map((f) => (
            <button type="button" key={f.id} className="fchip" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
              <f.icon aria-hidden="true" size={20} /> {f.label}
            </button>
          ))}
        </div>
      )}

      {GAME_GROUPS.map((gr) => {
        const gs = gr.games.filter(ok);
        if (!gs.length) return null;
        return (
          <section key={gr.id} aria-labelledby={`gg-${gr.id}`}>
            <div className="sec-head">
              <h2 id={`gg-${gr.id}`} className="sec-title">{gr.title}</h2>
              <small className="muted">{gr.sub}</small>
            </div>
            <div className="gtiles" data-n={gs.length} data-stagger>
              {gs.map((g) => <GameTile key={g} game={g} rule={rules[g]} />)}
            </div>
          </section>
        );
      })}
      {meta.loading && <Spinner />}
      {!meta.loading && shown.length === 0 && <div className="card center">Ingen spil passer til det valg. Prøv “Alle spil”.</div>}
      <p className="cap-note"><LuCoins aria-hidden="true" size={18} /> Op til {meta.data?.dailyCoinCap ?? 250} mønter om dagen</p>
    </div>
  );
}

export function GameSetup({ game }: { game: string }) {
  const { me, toast } = useApp();
  const m = GAME_META[game];
  const [grade, setGrade] = useState<number>(me!.grade ?? 5);
  const meta = useLoad(() => get(`/api/meta?grade=${grade}`), [grade]);
  const [subjects, setSubjects] = useState<string[]>(() => {
    /* a link such as /spil/quizduel?fag=tysk starts with just that subject chosen */
    const f = new URLSearchParams(location.search).get("fag");
    return f && SUBJECTS.some((x) => x.id === f) ? [f] : SUBJECTS.map((x) => x.id);
  });
  const [difficulty, setDifficulty] = useState<"mix" | 1 | 2 | 3>("mix");
  const [tdMap, setTdMap] = useState<MapId>(DEFAULT_MAP);
  const [tdDiff, setTdDiff] = useState<1 | 2 | 3>(2);
  const [mode, setMode] = useState<string>("");
  const [botCount, setBotCount] = useState(1);
  const [mpRounds, setMpRounds] = useState(20);
  const [botLevel, setBotLevel] = useState<"easy" | "medium" | "hard">("medium");
  const [practice, setPractice] = useState(false);
  const [rounds, setRounds] = useState(8);
  const [timer, setTimer] = useState<number>(0);
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  if (!m || !(GAME_IDS as readonly string[]).includes(game)) return <div className="card">Det spil findes ikke. <Link to="/spil">Se alle spil</Link></div>;
  const rule = meta.data?.rules?.[game];
  const modes: string[] = rule ? [...(rule.solo ? ["solo"] : []), ...(rule.bot ? ["bot"] : []), ...(rule.online ? ["quick", "lobby"] : [])] : [];
  const chosen = mode || modes[0] || "";
  const pool: Record<string, number> = meta.data?.pool ?? {};
  const sel = subjects.length ? subjects : [];
  const total = sel.reduce((a, s) => a + (pool[s] ?? 0), 0);
  const turnGame = ["kortkamp", "slagskibe", "fire", "skak", "kryds", "monopoly", "ludo", "yatzy"].includes(game);
  const preview = meta.data?.preview?.[practice ? "practice" : chosen === "solo" ? "solo" : chosen === "bot" ? "bot" : "online"];

  async function start(e: any) {
    e.preventDefault();
    if (!sel.length) return toast("Vælg mindst ét fag.", "error");
    setBusy(true);
    setErr(null);
    try {
      const body: any = { game, subjects: sel, grade, difficulty: game === "taarn" ? tdDiff : difficulty, mode: chosen, botLevel, practice: turnGame ? practice : undefined };
      if (game === "taarn") body.map = tdMap;
      if (game === "quizduel") body.rounds = rounds;
      if (game === "monopoly") body.rounds = mpRounds;
      if (chosen === "bot" && (rule?.max ?? 2) > 2) body.botCount = botCount;
      if (timer) body.timerSec = timer;
      const r = await post<{ id: string }>("/api/matches", body);
      go(game === "verden" ? "/verden" : `/kamp/${r.id}`);
    } catch (e2) {
      if (e2 instanceof ApiError && e2.code === "active_match") {
        go(`/kamp/${e2.data.matchId}`);
        return;
      }
      setErr(e2 instanceof ApiError ? e2 : new ApiError(0, "Noget gik galt."));
    } finally {
      setBusy(false);
    }
  }
  async function join(e: any) {
    e.preventDefault();
    try {
      const r = await post<{ id: string }>("/api/matches/join", { code: joinCode });
      go(`/kamp/${r.id}`);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2 : new ApiError(0, "Noget gik galt."));
    }
  }
  return (
    <div className="stack rise">
      <section className="setup-hero" style={{ ["--c1" as any]: m.c1, ["--c2" as any]: m.c2 }} aria-labelledby="sh-h" data-spot>
        <span className="sh-mark" aria-hidden="true">{(() => { const I = GAME_ICONS[game]!; return <I size={190} aria-hidden="true" />; })()}</span>
        <Sparkles n={14} />
        <Floaties kinds={["star", "coin", "cube"]} n={4} seed={game.length} />
        <Link to="/spil" className="sh-back"><LuArrowLeft aria-hidden="true" size={18} /> Alle spil</Link>
        <span className="sh-badge" aria-hidden="true">{(() => { const I = GAME_ICONS[game]!; return <I size={52} aria-hidden="true" />; })()}</span>
        <div className="sh-text">
          <h1 id="sh-h" className="title-game">{m.name}</h1>
          <p>{m.tagline}</p>
        </div>
      </section>
      <div className="setup-intro">
        <div className="setup-left">
          <HowTo game={game} big />
          <details className="rules"><summary><LuInfo aria-hidden="true" size={16} /> Regler</summary><p>{m.how}</p><ul>{(GAMES.find((x) => x.id === game)?.rules ?? []).map((r, i) => <li key={i}>{r}</li>)}</ul></details>
        </div>
        <GameVideo game={game} name={m.name} />
      </div>
      <form className="card stack" onSubmit={start}>
        <h2 className="lobby-title title-game" style={{ fontSize: "clamp(1.5rem, 4vw, 2.1rem)" }}>Vælg spiltilstand</h2>
        <div className="modes" role="group" aria-label="Spiltilstand">
          {modes.map((x) => (
            <button type="button" key={x} className="mode" aria-pressed={chosen === x} onClick={() => setMode(x)}>
              <span className="gameicon" style={{ ["--c1" as any]: { solo: "#8cf26a", bot: "#c39cff", quick: "#ffb347", lobby: "#6fd6ff" }[x] }} aria-hidden="true">{{ solo: <LuUser />, bot: <LuBot />, quick: <LuZap />, lobby: <LuUsers /> }[x]}</span>
              <span><b>{{ solo: "Alene", bot: "Mod computer", quick: "Hurtig kamp", lobby: "Spil med venner" }[x]}</b><small>{{ solo: "Øv dig", bot: "Spil mod en bot", quick: "Online", lobby: "Med en kode" }[x]}</small></span>
            </button>
          ))}
        </div>
        {game === "taarn" && <MapPicker value={tdMap} onChange={setTdMap} />}
        {game === "taarn" && <DifficultyPicker value={tdDiff} onChange={setTdDiff} />}
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend><span className="section-label">Hvad vil du øve?</span></legend>
          <div className="subjgrid">
            {SUBJECTS.map((s) => {
              const on = subjects.includes(s.id);
              return (
                <button type="button" key={s.id} className="subj" style={{ ["--sc" as any]: s.color }} aria-pressed={on} onClick={() => setSubjects(on ? subjects.filter((x) => x !== s.id) : [...subjects, s.id])}>
                  <span className="dot" aria-hidden="true" />
                  <span>{s.name}</span>
                </button>
              );
            })}
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <button type="button" className="btn ghost small" onClick={() => setSubjects(SUBJECTS.map((s) => s.id))}>Vælg alle</button>
            <button type="button" className="btn ghost small" onClick={() => setSubjects([])}>Ryd</button>
            {sel.length > 0 && total < 6 && <span className="err">Der er for få spørgsmål i de valgte fag til dette klassetrin.</span>}
          </div>
        </fieldset>
        {chosen === "bot" && (
          <div>
            <div className="segment" role="group" aria-label="Computerens niveau">
              {(["easy", "medium", "hard"] as const).map((l) => <button type="button" key={l} aria-pressed={botLevel === l} onClick={() => setBotLevel(l)}>{{ easy: "Begynder-bot", medium: "Mellem-bot", hard: "Mester-bot" }[l]}</button>)}
            </div>
            {(rule?.max ?? 2) > 2 && (
              <div style={{ marginTop: 10 }}>
                <span className="section-label">Antal computer-modspillere</span>
                <div className="segment" role="group" aria-label="Antal computer-modspillere">
                  {Array.from({ length: (rule?.max ?? 2) - 1 }, (_, i) => i + 1).map((n) => <button type="button" key={n} aria-pressed={botCount === n} onClick={() => setBotCount(n)}>{n}</button>)}
                </div>
              </div>
            )}
            <p className="muted">Computeren er altid en bot.</p>
          </div>
        )}
        <details className="more"><summary>Flere valg</summary>
        <div className="stack">
        <div className="grid cols-2">
          <div>
            <span className="section-label">Vælg klassetrin</span>
            <select aria-label="Klassetrin" value={grade} onChange={(e: any) => setGrade(Number(e.target.value))}>
              {GRADES.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}
            </select>
            <small className="muted">Spørgsmål tilpasses omkring dette klassetrin (±2).</small>
          </div>
          {game !== "taarn" && <div>
            <span className="section-label">Sværhedsgrad</span>
            <div className="segment" role="group" aria-label="Sværhedsgrad">
              {(["mix", 1, 2, 3] as const).map((d) => <button type="button" key={String(d)} aria-pressed={difficulty === d} onClick={() => setDifficulty(d)}>{d === "mix" ? "Blandet" : DIFFICULTY_LABEL[d]}</button>)}
            </div>
          </div>}
        </div>
        {chosen === "quick" && <p className="muted">Vi finder en modstander.</p>}
        {chosen === "lobby" && <p className="muted">Du får en kode til din ven.</p>}
        {turnGame && (
          <label className="check"><input type="checkbox" checked={practice} onChange={(e: any) => setPractice(e.target.checked)} /><span>Øvetilstand <small className="muted">(ingen straf, halvt antal mønter)</small></span></label>
        )}
        {game === "monopoly" && (
          <label className="field">Antal runder: {mpRounds} <small className="muted">(den rigeste vinder)</small><input type="range" min={5} max={40} value={mpRounds} onChange={(e: any) => setMpRounds(Number(e.target.value))} /></label>
        )}
        {game === "quizduel" && (
          <label className="field">Antal runder: {rounds}<input type="range" min={3} max={15} value={rounds} onChange={(e: any) => setRounds(Number(e.target.value))} /></label>
        )}
        {(turnGame || game === "speedrun" || game === "quizduel") && (
          <label className="field">{game === "speedrun" ? "Tid i alt" : "Tid pr. spørgsmål"}
            <select value={timer} onChange={(e: any) => setTimer(Number(e.target.value))}>
              <option value={0}>Standard</option>
              {(game === "speedrun" ? [30, 60, 90, 120] : [15, 20, 30, 45, 60]).map((s) => <option key={s} value={s}>{s} sekunder</option>)}
            </select>
          </label>
        )}
        </div>
        </details>
        {preview && (
          <div className="earn" aria-live="polite" aria-label="Det kan du optjene">
            <span className="earn-chip"><Coin /><b>{preview.perCorrect[0].coins}–{preview.perCorrect[2].coins}</b><small>pr. svar</small></span>
            <span className="earn-chip"><LuFlag aria-hidden="true" size={16} /><b>{preview.completion}</b><small>i mål</small></span>
            {preview.win ? <span className="earn-chip"><LuTrophy aria-hidden="true" size={16} /><b>{preview.win}</b><small>sejr</small></span> : null}
            <span className="earn-chip dim"><LuCoins aria-hidden="true" size={16} /><b>{meta.data?.coinsToday ?? 0}/{meta.data?.dailyCoinCap ?? 250}</b><small>i dag</small></span>
            {preview.note ? <details className="earn-note"><summary aria-label="Mere om mønter"><LuInfo aria-hidden="true" size={16} /></summary><p>{preview.note}</p></details> : null}
          </div>
        )}
        <ErrorBox error={err} />
        <div className="startbar"><button className="btn cta block" disabled={busy || !chosen || meta.loading}>{busy ? "Starter…" : chosen === "lobby" ? "Opret rum" : chosen === "quick" ? "Find modstander" : "▶ Start spillet"}</button></div>
      </form>
      {rule?.online && (
        <form className="card row" onSubmit={join}>
          <label className="field" style={{ flex: 1, minWidth: 200 }}>Har du en rumkode?
            <input value={joinCode} onChange={(e: any) => setJoinCode(e.target.value.toUpperCase())} maxLength={8} placeholder="fx K7P3Q" autoComplete="off" />
          </label>
          <button className="btn violet" disabled={joinCode.length < 4}>Deltag</button>
        </form>
      )}
      {meta.loading && <Spinner />}
    </div>
  );
}
